import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import initService from '../services/initService';
import { getCurrentUser } from '../services/authService';

const InitContext = createContext();

export const useInit = () => {
  const context = useContext(InitContext);
  if (!context) {
    throw new Error('useInit must be used within an InitProvider');
  }
  return context;
};

export const InitProvider = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState(null);
  const [lastInitTime, setLastInitTime] = useState(null);
  const hasInitialized = useRef(false); // 防止 React Strict Mode 重复执行

  // 执行初始化
  const performInit = async () => {
    if (isInitializing) return; // 防止重复初始化
    
    setIsInitializing(true);
    setInitError(null);
    
    try {
      const result = await initService.initializeAfterLogin();
      
      if (result.success) {
        setIsInitialized(true);
        setLastInitTime(new Date());
        console.log('初始化成功:', result.data);
      } else {
        setInitError(result.error);
        console.warn('初始化失败:', result.error);
      }
    } catch (error) {
      setInitError(error.message);
      console.error('初始化异常:', error);
    } finally {
      setIsInitializing(false);
    }
  };

  // 检查是否需要初始化
  const checkAndInit = async () => {
    const currentUser = getCurrentUser();

    if (!currentUser || !currentUser.id) {
      console.log('未获取到用户信息，跳过初始化检查');
      setIsInitialized(false);
      return;
    }

    if (initService.needsReinitialization()) {
      console.log('检测到需要初始化，开始执行...');
      await performInit();
    } else {
      console.log('数据已是最新，无需重新初始化');
      setIsInitialized(true);
    }
  };

  // 强制重新初始化
  const forceReinit = async () => {
    console.log('强制重新初始化...');
    await performInit();
  };

  // 组件挂载时检查初始化
  useEffect(() => {
    // 防止 React Strict Mode 导致的重复执行
    if (hasInitialized.current) {
      console.log('已经初始化过，跳过重复执行');
      return;
    }
    hasInitialized.current = true;
    checkAndInit();
  }, []);

  // 定期检查是否需要重新初始化（每5分钟检查一次）
  useEffect(() => {
    const interval = setInterval(() => {
      if (isInitialized && !isInitializing) {
        checkAndInit();
      }
    }, 5 * 60 * 1000); // 5分钟

    return () => clearInterval(interval);
  }, [isInitialized, isInitializing]);

  const value = {
    isInitialized,
    isInitializing,
    initError,
    lastInitTime,
    performInit,
    checkAndInit,
    forceReinit
  };

  return (
    <InitContext.Provider value={value}>
      {children}
    </InitContext.Provider>
  );
}; 